import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp,
  where
} from 'firebase/firestore';
import { trackFeedbackSubmission, getSessionId, getCurrentPage } from './analytics';

// Feedback types
export const FEEDBACK_TYPES = {
  BUG: 'bug',
  FEATURE: 'feature',
  GENERAL: 'general',
  CALCULATION: 'calculation',
  UI_UX: 'ui_ux',
  PERFORMANCE: 'performance'
};

// Feedback ratings
export const FEEDBACK_RATINGS = {
  VERY_POOR: 1,
  POOR: 2,
  NEUTRAL: 3,
  GOOD: 4,
  EXCELLENT: 5
};

// Submit general feedback
export const submitFeedback = async (feedbackData) => {
  try {
    const {
      userId = null,
      userEmail = null,
      feedbackType,
      rating,
      message,
      page = getCurrentPage(),
      userAgent = navigator.userAgent,
      sessionId = getSessionId(),
      resolved = false
    } = feedbackData;

    // Validate required fields
    if (!feedbackType || !message) {
      throw new Error('Feedback type and message are required');
    }

    if (rating && (rating < 1 || rating > 5)) {
      throw new Error('Rating must be between 1 and 5');
    }

    const feedback = {
      userId,
      userEmail,
      feedbackType,
      rating: rating || null,
      message: message.trim(),
      page,
      userAgent,
      sessionId,
      resolved,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'feedback'), feedback);
    
    // Track feedback submission in analytics
    trackFeedbackSubmission(feedbackType, rating, {
      feedback_id: docRef.id,
      message_length: message.length,
      has_email: !!userEmail
    });

    console.log('[Feedback] Submitted successfully:', docRef.id);
    return { success: true, id: docRef.id };

  } catch (error) {
    console.error('[Feedback] Error submitting feedback:', error);
    return { success: false, error: error.message };
  }
};

// Submit page-specific feedback
export const submitPageFeedback = async (pageData) => {
  try {
    const {
      page = getCurrentPage(),
      userId = null,
      helpful,
      suggestions = '',
      sessionId = getSessionId()
    } = pageData;

    if (helpful === undefined || helpful === null) {
      throw new Error('Helpful rating is required for page feedback');
    }

    const pageFeedback = {
      page,
      userId,
      helpful: Boolean(helpful),
      suggestions: suggestions.trim(),
      sessionId,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'page_feedback'), pageFeedback);
    
    // Track in analytics
    trackFeedbackSubmission('page_helpful', helpful ? 5 : 1, {
      feedback_id: docRef.id,
      has_suggestions: !!suggestions,
      page: page
    });

    console.log('[Page Feedback] Submitted successfully:', docRef.id);
    return { success: true, id: docRef.id };

  } catch (error) {
    console.error('[Page Feedback] Error submitting:', error);
    return { success: false, error: error.message };
  }
};

// Record user session data
export const recordUserSession = async (sessionData) => {
  try {
    const {
      sessionId = getSessionId(),
      userId = null,
      startTime,
      endTime = null,
      pagesVisited = [],
      calculationsCompleted = 0,
      scenariosCreated = 0,
      feedbackSubmitted = false,
      exitPage = getCurrentPage()
    } = sessionData;

    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    const session = {
      sessionId,
      userId,
      startTime: startTime || new Date().toISOString(),
      endTime,
      pagesVisited,
      calculationsCompleted,
      scenariosCreated,
      feedbackSubmitted,
      exitPage,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'user_sessions'), session);
    
    console.log('[User Session] Recorded successfully:', docRef.id);
    return { success: true, id: docRef.id };

  } catch (error) {
    console.error('[User Session] Error recording:', error);
    return { success: false, error: error.message };
  }
};

// Update existing session
export const updateUserSession = async (sessionId, updateData) => {
  try {
    const sessionQuery = query(
      collection(db, 'user_sessions'),
      where('sessionId', '==', sessionId)
    );
    
    const querySnapshot = await getDocs(sessionQuery);
    
    if (querySnapshot.empty) {
      throw new Error('Session not found');
    }

    const sessionDoc = querySnapshot.docs[0];
    await updateDoc(sessionDoc.ref, {
      ...updateData,
      updatedAt: new Date().toISOString()
    });

    console.log('[User Session] Updated successfully:', sessionId);
    return { success: true };

  } catch (error) {
    console.error('[User Session] Error updating:', error);
    return { success: false, error: error.message };
  }
};

// Get recent feedback (admin function)
export const getRecentFeedback = async (limitCount = 50) => {
  try {
    const feedbackQuery = query(
      collection(db, 'feedback'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(feedbackQuery);
    const feedback = [];

    querySnapshot.forEach((doc) => {
      feedback.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, feedback };

  } catch (error) {
    console.error('[Feedback] Error getting recent feedback:', error);
    return { success: false, error: error.message };
  }
};

// Get feedback by type (admin function)
export const getFeedbackByType = async (feedbackType, limitCount = 30) => {
  try {
    const feedbackQuery = query(
      collection(db, 'feedback'),
      where('feedbackType', '==', feedbackType),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(feedbackQuery);
    const feedback = [];

    querySnapshot.forEach((doc) => {
      feedback.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, feedback };

  } catch (error) {
    console.error('[Feedback] Error getting feedback by type:', error);
    return { success: false, error: error.message };
  }
};

// Mark feedback as resolved (admin function)
export const resolveFeedback = async (feedbackId) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    await updateDoc(feedbackRef, {
      resolved: true,
      resolvedAt: new Date().toISOString()
    });

    console.log('[Feedback] Marked as resolved:', feedbackId);
    return { success: true };

  } catch (error) {
    console.error('[Feedback] Error resolving feedback:', error);
    return { success: false, error: error.message };
  }
};

// Get page feedback analytics
export const getPageFeedbackAnalytics = async (page = null) => {
  try {
    let feedbackQuery;
    
    if (page) {
      feedbackQuery = query(
        collection(db, 'page_feedback'),
        where('page', '==', page),
        orderBy('timestamp', 'desc')
      );
    } else {
      feedbackQuery = query(
        collection(db, 'page_feedback'),
        orderBy('timestamp', 'desc')
      );
    }

    const querySnapshot = await getDocs(feedbackQuery);
    const feedback = [];
    let helpfulCount = 0;
    let notHelpfulCount = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      feedback.push({
        id: doc.id,
        ...data
      });

      if (data.helpful) {
        helpfulCount++;
      } else {
        notHelpfulCount++;
      }
    });

    const total = helpfulCount + notHelpfulCount;
    const helpfulPercentage = total > 0 ? Math.round((helpfulCount / total) * 100) : 0;

    return {
      success: true,
      feedback,
      analytics: {
        total,
        helpful: helpfulCount,
        notHelpful: notHelpfulCount,
        helpfulPercentage
      }
    };

  } catch (error) {
    console.error('[Page Feedback] Error getting analytics:', error);
    return { success: false, error: error.message };
  }
};

// Utility function to validate feedback data
export const validateFeedbackData = (feedbackData) => {
  const errors = [];

  if (!feedbackData.feedbackType) {
    errors.push('Feedback type is required');
  }

  if (!feedbackData.message || feedbackData.message.trim().length === 0) {
    errors.push('Message is required');
  }

  if (feedbackData.message && feedbackData.message.length > 2000) {
    errors.push('Message must be less than 2000 characters');
  }

  if (feedbackData.rating && (feedbackData.rating < 1 || feedbackData.rating > 5)) {
    errors.push('Rating must be between 1 and 5');
  }

  if (feedbackData.userEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(feedbackData.userEmail)) {
    errors.push('Invalid email format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  submitFeedback,
  submitPageFeedback,
  recordUserSession,
  updateUserSession,
  getRecentFeedback,
  getFeedbackByType,
  resolveFeedback,
  getPageFeedbackAnalytics,
  validateFeedbackData,
  FEEDBACK_TYPES,
  FEEDBACK_RATINGS
};